import { createReducer, on } from '@ngrx/store';
import { ContribuyenteWizardActions } from './contribuyente-wizard.actions';
import {
    ContribuyenteWizardState,
    initialContribuyenteWizardState,
    WizardStep
} from './contribuyente-wizard.state';

export const contribuyenteWizardReducer = createReducer(
    initialContribuyenteWizardState,

    // ==================== Navigation ====================
    on(ContribuyenteWizardActions.nextStep, (state): ContribuyenteWizardState => ({
        ...state,
        currentStep: Math.min(state.currentStep + 1, WizardStep.FACTURACION_PAGO) as WizardStep
    })),

    on(ContribuyenteWizardActions.previousStep, (state): ContribuyenteWizardState => ({
        ...state,
        currentStep: Math.max(state.currentStep - 1, WizardStep.INFO_CONTRIBUYENTE) as WizardStep
    })),

    on(ContribuyenteWizardActions.goToStep, (state, { step }): ContribuyenteWizardState => ({
        ...state,
        currentStep: step
    })),

    on(ContribuyenteWizardActions.resetWizard, (): ContribuyenteWizardState => ({
        ...initialContribuyenteWizardState
    })),

    // ==================== Step 1: Info Contribuyente ====================
    on(ContribuyenteWizardActions.updateInfoContribuyente, (state, action): ContribuyenteWizardState => ({
        ...state,
        infoContribuyente: {
            ...state.infoContribuyente,
            ...(action.rfc !== undefined && { rfc: action.rfc }),
            ...(action.razonSocial !== undefined && { razonSocial: action.razonSocial }),
            ...(action.regimenCapital !== undefined && { regimenCapital: action.regimenCapital }),
            ...(action.logoUrl !== undefined && { logoUrl: action.logoUrl }),
            ...(action.usarDatosUsuarioPropietario !== undefined && { usarDatosUsuarioPropietario: action.usarDatosUsuarioPropietario }),
            ...(action.nombreContacto !== undefined && { nombreContacto: action.nombreContacto }),
            ...(action.emailContacto !== undefined && { emailContacto: action.emailContacto }),
            ...(action.telefonoContacto !== undefined && { telefonoContacto: action.telefonoContacto }),
        }
    })),

    on(ContribuyenteWizardActions.updateDireccionFiscal, (state, { direccion }): ContribuyenteWizardState => ({
        ...state,
        infoContribuyente: {
            ...state.infoContribuyente,
            direccionFiscal: {
                ...state.infoContribuyente.direccionFiscal,
                ...direccion
            }
        }
    })),

    on(ContribuyenteWizardActions.addRegimenFiscal, (state, { regimen }): ContribuyenteWizardState => {
        const existingRegimenes = state.infoContribuyente.regimenesFiscales;
        if (existingRegimenes.find(r => r.codigo === regimen.codigo)) {
            return state; // Already exists
        }
        const newRegimen = {
            ...regimen,
            esPrincipal: existingRegimenes.length === 0
        };
        return {
            ...state,
            infoContribuyente: {
                ...state.infoContribuyente,
                regimenesFiscales: [...existingRegimenes, newRegimen]
            }
        };
    }),

    on(ContribuyenteWizardActions.removeRegimenFiscal, (state, { codigo }): ContribuyenteWizardState => {
        let regimenes = state.infoContribuyente.regimenesFiscales.filter(r => r.codigo !== codigo);
        // If we removed the principal, make the first one principal
        if (regimenes.length > 0 && !regimenes.some(r => r.esPrincipal)) {
            regimenes = regimenes.map((r, i) => ({ ...r, esPrincipal: i === 0 }));
        }
        return {
            ...state,
            infoContribuyente: {
                ...state.infoContribuyente,
                regimenesFiscales: regimenes
            }
        };
    }),

    on(ContribuyenteWizardActions.setRegimenesFiscales, (state, { regimenes }): ContribuyenteWizardState => ({
        ...state,
        infoContribuyente: {
            ...state.infoContribuyente,
            regimenesFiscales: regimenes
        }
    })),

    // ==================== Step 2: Certificados ====================
    on(ContribuyenteWizardActions.updateCSD, (state, { data }): ContribuyenteWizardState => ({
        ...state,
        certificados: {
            ...state.certificados,
            csd: {
                ...state.certificados.csd,
                ...data
            }
        }
    })),

    on(ContribuyenteWizardActions.updateFIEL, (state, { data }): ContribuyenteWizardState => ({
        ...state,
        certificados: {
            ...state.certificados,
            fiel: {
                ...state.certificados.fiel,
                ...data
            }
        }
    })),

    on(ContribuyenteWizardActions.setCSDFiles, (state, { cerFile, keyFile }): ContribuyenteWizardState => ({
        ...state,
        certificados: {
            ...state.certificados,
            csd: {
                ...state.certificados.csd,
                cerFileName: cerFile?.name || null,
                keyFileName: keyFile?.name || null
            }
        }
    })),

    on(ContribuyenteWizardActions.setFIELFiles, (state, { cerFile, keyFile }): ContribuyenteWizardState => ({
        ...state,
        certificados: {
            ...state.certificados,
            fiel: {
                ...state.certificados.fiel,
                cerFileName: cerFile?.name || null,
                keyFileName: keyFile?.name || null
            }
        }
    })),

    // ==================== Step 3: Facturacion ====================
    on(ContribuyenteWizardActions.updateFacturacionPago, (state, { data }): ContribuyenteWizardState => ({
        ...state,
        facturacionPago: {
            ...state.facturacionPago,
            ...data
        }
    })),

    on(ContribuyenteWizardActions.updateDatosTarjeta, (state, action): ContribuyenteWizardState => ({
        ...state,
        facturacionPago: {
            ...state.facturacionPago,
            datosTarjeta: {
                ...state.facturacionPago.datosTarjeta,
                ...(action.nombreTitular !== undefined && { nombreTitular: action.nombreTitular }),
                ...(action.numeroTarjeta !== undefined && { numeroTarjeta: action.numeroTarjeta }),
                ...(action.fechaExpiracion !== undefined && { fechaExpiracion: action.fechaExpiracion }),
                ...(action.cvv !== undefined && { cvv: action.cvv }),
            }
        }
    })),

    // ==================== API Actions ====================
    on(ContribuyenteWizardActions.submitWizard, (state): ContribuyenteWizardState => ({
        ...state,
        isLoading: true,
        error: null
    })),

    on(ContribuyenteWizardActions.submitWizardSuccess, (state, { empresa }): ContribuyenteWizardState => ({
        ...state,
        isLoading: false,
        createdEmpresa: empresa,
        isCompleted: true,
        error: null
    })),

    on(ContribuyenteWizardActions.submitWizardFailure, (state, { error }): ContribuyenteWizardState => ({
        ...state,
        isLoading: false,
        error
    })),

    // Update (Edit mode)
    on(ContribuyenteWizardActions.updateWizard, (state): ContribuyenteWizardState => ({
        ...state,
        isLoading: true,
        error: null
    })),

    on(ContribuyenteWizardActions.updateWizardSuccess, (state, { empresa }): ContribuyenteWizardState => ({
        ...state,
        isLoading: false,
        createdEmpresa: empresa,
        isCompleted: true,
        error: null
    })),

    on(ContribuyenteWizardActions.updateWizardFailure, (state, { error }): ContribuyenteWizardState => ({
        ...state,
        isLoading: false,
        error
    })),

    // ==================== Loading/Error ====================
    on(ContribuyenteWizardActions.setLoading, (state, { isLoading }): ContribuyenteWizardState => ({
        ...state,
        isLoading
    })),

    on(ContribuyenteWizardActions.setError, (state, { error }): ContribuyenteWizardState => ({
        ...state,
        error
    })),

    on(ContribuyenteWizardActions.clearError, (state): ContribuyenteWizardState => ({
        ...state,
        error: null
    }))
);
